import { useCustomAllocationDraft } from '@components/CustomAllocationEditor/useCustomAllocationDraft';
import { useRejectOutOfRange } from '@components/CustomAllocationEditor/useRejectOutOfRange';
import { PortfolioAllocationGrid } from '@components/PortfolioAllocationGrid';
import {
  buildAssetClassGroupView,
  buildPortfolioAllocationView,
} from '@components/PortfolioAllocationGrid/buildPortfolioAllocationView';
import { useCollapsedAssetGroups } from '@components/PortfolioAllocationGrid/useCollapsedAssetGroups';
import { isAssetClassAllocationModel } from '@ovComponents/4-module/widgets/portfolio/components/buildCustomAllocationInput';
import { tradeableAllocationSubAccounts } from '@ovComponents/4-module/widgets/portfolio/components/tradeableAllocationMembers';
import { TESTID_PORTFOLIO_WIDGET_RECALCULATE_ALLOCATIONS_BUTTON } from '@ovComponents/4-module/widgets/portfolio/portfolioTestIds';
import { Box, Button, Stack, Typography, useTheme } from 'hephaestus/web';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../hooks/useLocalization';
import { AccountAllocationLeftHead } from './AccountAllocationLeftHead';
import { AccountAllocationLegend } from './AccountAllocationLegend';
import {
  accountAllocationRows,
  buildAccountAllocationSlots,
} from './buildAccountAllocationSlots';
import { buildDraftMatrix } from './placeholderMatrix';
import type { AccountAllocationGridProp } from './types';

// The workflow never confirms through the draft: Continue hands the matrix to
// onSave for staging instead.
const NO_CONFIRM = () => {};

export const AccountAllocationGrid = ({
  report,
  onSave,
  readOnly = false,
  footerActions,
  initialDraft,
}: AccountAllocationGridProp) => {
  const { t } = useTranslation();
  const { translateBackendString } = useLocalization();
  const { collapsedAssetGroups, toggleAssetGroupCollapse } =
    useCollapsedAssetGroups();
  const isAssetClassPortfolio = isAssetClassAllocationModel(report);
  const rejectOutOfRange = useRejectOutOfRange();
  const theme = useTheme();
  const draft = useCustomAllocationDraft(report, { onConfirm: NO_CONFIRM });
  const [continuing, setContinuing] = useState(false);

  // Declared after the draft hook, so it runs after the draft's own reset on
  // a new report and the restored edits win.
  const { restoreDraft } = draft;
  const restoredReportId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!initialDraft || restoredReportId.current === report.id) return;
    restoredReportId.current = report.id;
    restoreDraft(initialDraft);
  }, [initialDraft, report.id, restoreDraft]);

  // One source for the cell colours and the legend swatches, so they always
  // match whatever theme the organization uses.
  const stateTones = useMemo(
    () => ({
      locked: { border: theme.color.warning, fill: theme.color.warningVariant },
      uploaded: {
        border: theme.color.positive,
        fill: theme.color.positiveVariant,
      },
    }),
    [theme.color],
  );

  // The range check lives here, not in the shared draft, so the Portfolio tab
  // keeps accepting what it accepts today.
  const commitCell = useCallback(
    (subAccountId: string, financialProductId: string, fraction: number) => {
      if (rejectOutOfRange([fraction])) return;
      draft.commitCellEdit(subAccountId, financialProductId, fraction);
    },
    [draft, rejectOutOfRange],
  );

  // Held disabled until the caller finishes, so a double click can't stage
  // twice or advance the workflow twice.
  const handOver = useCallback(async () => {
    setContinuing(true);
    try {
      await onSave({
        matrix: buildDraftMatrix(report, draft.displayFraction),
        lockedCells: [...draft.lockedCells.keys()],
      });
    } finally {
      setContinuing(false);
    }
  }, [onSave, report, draft]);

  const { subAccounts, securities } = useMemo(
    () =>
      buildPortfolioAllocationView({
        portfolioReport: report,
        t,
        translateBackendString,
      }),
    [report, t, translateBackendString],
  );
  const rows = useMemo(() => accountAllocationRows(securities), [securities]);
  const assetClassGroups = useMemo(
    () =>
      buildAssetClassGroupView({
        securities: rows,
        isAssetClassPortfolio,
        t,
        translateBackendString,
      }),
    [rows, isAssetClassPortfolio, t, translateBackendString],
  );
  const hasNonTradeableAccount =
    tradeableAllocationSubAccounts(report.subAccounts).length <
    report.subAccounts.length;

  return (
    <Stack spacing={2}>
      {!readOnly && (
        <Stack
          direction='row'
          spacing={2}
          useFlexGap
          sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <AccountAllocationLegend
            label={t('components:accountAllocationGrid.legend.label')}
            items={[
              {
                label: t('components:accountAllocationGrid.legend.locked'),
                tone: stateTones.locked,
              },
              {
                label: t('components:accountAllocationGrid.legend.uploaded'),
                tone: stateTones.uploaded,
              },
            ]}
          />
          <Stack direction='row' spacing={2}>
            <Button
              type='button'
              variant='outlined'
              disabled={!draft.hasUnsavedWork || continuing}
              onClick={draft.resetDraft}
            >
              {t('components:accountAllocationGrid.undo')}
            </Button>
            <Button
              type='button'
              variant='outlined'
              disabled={!draft.needsRecalculation || continuing}
              onClick={draft.recalculateAll}
              data-testid={
                TESTID_PORTFOLIO_WIDGET_RECALCULATE_ALLOCATIONS_BUTTON
              }
            >
              {t('components:accountAllocationGrid.recalculate')}
            </Button>
          </Stack>
        </Stack>
      )}
      {hasNonTradeableAccount && (
        <Typography variant='bodySmall' component='p'>
          {t('components:accountAllocationGrid.nonTradeableNote')}
        </Typography>
      )}
      <PortfolioAllocationGrid
        securities={rows}
        subAccounts={subAccounts}
        assetClassGroups={assetClassGroups}
        isSubAccountVisible={() => true}
        showTargetColumn={false}
        collapsedAssetGroups={collapsedAssetGroups}
        onToggleAssetGroup={toggleAssetGroupCollapse}
        leftTableHead={
          <AccountAllocationLeftHead
            isAssetClassPortfolio={isAssetClassPortfolio}
          />
        }
        slots={buildAccountAllocationSlots({
          report,
          securities: rows,
          isAssetClassPortfolio,
          // Frozen while Continue saves, so what's on screen is what's staged.
          readOnly: readOnly || continuing,
          draft,
          stateTones,
          onCommitCell: commitCell,
          t,
        })}
      />
      {!readOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {footerActions}
          <Button
            type='button'
            variant='contained'
            sx={{ mt: 3 }}
            disabled={!draft.canContinue || continuing}
            loading={continuing}
            onClick={handOver}
          >
            {t('shared:continue')}
          </Button>
        </Box>
      )}
    </Stack>
  );
};
